import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import Header from "./components/Header.js";
import HomePage from "./pages/HomePage.js";
import ActsPage from "./pages/Acts/ActsPage.js";
import AddActPage from "./pages/Acts/AddActPage.js";
import AboutPage from "./pages/About/AboutPage.js";
import ReportPage from "./pages/Report/ReportPage.js";
import RatingReportsPage from "./pages/Report/RatingReportsPage.js";
import StudentPage from "./pages/Student/StudentPage.js";
import StudentForm from "./pages/Student/StudentForm.js";
import StudentTransfer from "./pages/Student/StudentTransfer.js";
import StudentsByDormitory from "./pages/Student/StudentsByDormitory.js";
import ExpelStudentPage from "./pages/Student/ExpelStudentPage.js";
import ArchivedStudentsPage from "./pages/Student/ArchivedStudentsPage.js";
import RatingPage from "./pages/Rating/RatingPage.js";
import StudentRatingPage from "./pages/Rating/StudentRatingPage.js";
import StudentTransferTable from "./pages/Student/StudentTransferTable.js";
import ProposeAct from "./pages/Acts/ProposeAct.js";
import ReviewActs from "./pages/Acts/ReviewActs.js";

import AdminAct from "./pages/Admin/ViolationsPage.js";
import RoomsPage from "./pages/Admin/RoomsPage.js";
import DormitoriesPage from "./pages/Admin/DormitoriesPage.js";
import WeightsPage from "./pages/Admin/WeightsPage.js";
import AllocationPlanPage from "./pages/Allocation/AllocationPlanPage.js";
import AllocationImportPage from "./pages/Allocation/AllocationImportPage.js";
import StudentsImportPage from "./pages/Allocation/StudentsImportPage.js";
import ImportRevertPage from "./pages/Allocation/ImportRevertPage.js";
import FreeRoomsPage from "./pages/Allocation/FreeRoomsPage.js";
import RoomDetailsPage from "./pages/Allocation/RoomDetailsPage.js";
import StudentDecisionPage from "./pages/Student/StudentDecisionPage.js";

import "./styles/theme/variables.css";

import LoginPage from "./pages/Login/LoginPage.js";
import ProtectedRoute from "./components/ProtectedRoute.js";
import { AuthProvider, useAuth } from "./pages/Login/AuthContext.js";

const API = process.env.REACT_APP_API_BASE;

const ZppgyRoute = ({ children }) => {
  const { user } = useAuth();
  return user?.role === "zppgy" ? children : <Navigate to="/propese-add" />;
};

// Функція для правильного редіректу залежно від ролі
const RedirectByRole = () => {
  const { user } = useAuth();
  if (user?.role === "admin") return <Navigate to="/" />;
  if (user?.role === "zppgy") return <Navigate to="/propese-add" />;
  return <Navigate to="/" />;
};
// Згорнуте бокове меню (десктоп) запам'ятовується в браузері: це лише зручність,
// тож недоступне сховище (приватний режим) просто означає «розгорнуте».
const SIDEBAR_KEY = "gurt.sidebarCollapsed";
const readSidebarCollapsed = () => {
  try {
    return window.localStorage.getItem(SIDEBAR_KEY) === "1";
  } catch {
    return false;
  }
};

const AppContent = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 770 );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const toggleSidebar = () =>
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(SIDEBAR_KEY, next ? "1" : "0");
      } catch {
        // сховище недоступне -- стан лишається лише до перезавантаження
      }
      return next;
    });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const location = useLocation();
  // Роль -- з AuthContext (пам'ять), а не зі сховища браузера.
  const { user, loading: authLoading } = useAuth();
  const isZppgy = user?.role === "zppgy";
  const [serverOnline, setServerOnline] = useState(true); // Стан для перевірки сервера
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkServer = async () => {
      try {
        const response = await fetch(`${API}/health`); // URL на сервері
        if (!response.ok) throw new Error("Сервер недоступний");
        setServerOnline(true);
      } catch (error) {
        setServerOnline(false);
      } finally {
        setLoading(false);
      }
    };

    checkServer();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        Підключення до сервера...
      </div>
    );
  }

  // Чекаємо на мовчазне відновлення сесії: частина маршрутів не обгорнута
  // в ProtectedRoute і без цього зробила б перші запити ще без токена, а
  // редірект path="*" спрацював би до того, як стала відома роль.
  if (authLoading) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        Перевірка сесії...
      </div>
    );
  }

  if (!serverOnline) {
    return (
      <div style={{ textAlign: "center", padding: "20px", color: "red" }}>
        Сервер недоступний. Будь ласка, спробуйте пізніше або зверніться до
        адміністратора.
      </div>
    );
  }

  const isLoginPage = location.pathname === "/login";
  const fullWidth = isLoginPage || isMobile || isZppgy || sidebarCollapsed;

  return (
    <div
      style={{
        marginLeft: fullWidth ? "0" : "220px",
        marginTop: isLoginPage ? "0" : "30px",
        padding: isLoginPage ? "0" : "5px",
        width: fullWidth ? "99%" : "83%",
        transition: "margin-left 0.25s ease",
        background: isLoginPage
          ? 'url("/image/backgroundImage.png") no-repeat center center/cover'
          : "var(--background-color)",
        display: isLoginPage ? "flex" : "block",
        alignItems: isLoginPage ? "center" : "",
        justifyContent: isLoginPage ? "center" : "",
      }}
    >
      <Header sidebarCollapsed={sidebarCollapsed} onToggleSidebar={toggleSidebar} />
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        {/* Спеціальний маршрут для zppgy */}
        <Route
          path="/propese-add"
          element={
            <ProtectedRoute>
              <ZppgyRoute>
                <ProposeAct />
              </ZppgyRoute>
            </ProtectedRoute>
          }
        />

        {/* Якщо zppgy спробує зайти кудись ще - редірект */}
        <Route
          path="*"
          element={
            <Navigate
              to={isZppgy ? "/propese-add" : "/"}
            />
          }
        />

        {/* Основні маршрути */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<RedirectByRole />} />

        <Route
          path="/admin-acts"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminAct />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-rooms"
          element={
            <ProtectedRoute requiredRole="admin">
              <RoomsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-dormitories"
          element={
            <ProtectedRoute requiredRole="admin">
              <DormitoriesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/weights"
          element={
            <ProtectedRoute requiredRole="admin">
              <WeightsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/allocation/plan"
          element={
            <ProtectedRoute requiredRole="admin">
              <AllocationPlanPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/allocation/import"
          element={
            <ProtectedRoute requiredRole="admin">
              <AllocationImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/allocation/import-revert"
          element={
            <ProtectedRoute requiredRole="admin">
              <ImportRevertPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/allocation/rooms"
          element={
            <ProtectedRoute requiredRole="admin">
              <FreeRoomsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/allocation/rooms/:id"
          element={
            <ProtectedRoute requiredRole="admin">
              <RoomDetailsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/allocation/import-students"
          element={
            <ProtectedRoute requiredRole="admin">
              <StudentsImportPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/other-gurt"
          element={
            <ProtectedRoute>
              <StudentsByDormitory />
            </ProtectedRoute>
          }
        />
        <Route path="/goout" element={<ExpelStudentPage />} />
        <Route path="/arhive" element={<ArchivedStudentsPage />} />
        <Route path="/acts" element={<ActsPage />} />
        <Route path="/new-act" element={<AddActPage />} />
        <Route path="/new" element={<StudentForm />} />
        <Route path="/transfer" element={<StudentTransfer />} />
        <Route path="/transfer-table" element={<StudentTransferTable />} />
        <Route path="/report" element={<ReportPage />} />
        <Route path="/report-rating" element={<RatingReportsPage />} />
        <Route path="/instruction" element={<AboutPage />} />
        <Route path="/student/:id" element={<StudentPage />} />
        <Route path="/student/:id/decision" element={<StudentDecisionPage />} />
        <Route path="/rating" element={<RatingPage />} />
        <Route path="/rating-add" element={<StudentRatingPage />} />
        <Route path="/rating-add" element={<StudentRatingPage />} />
        <Route path="/propese-act" element={<ProposeAct />} />
        <Route path="/propesed-act" element={<ReviewActs />} />
        <Route
          path="*"
          element={
            <div
              style={{ maxWidth: "400px", margin: "0 auto", padding: "20px" }}
            >
              <h1>Сторінку не знайдено! (Помилка 404)</h1>
            </div>
          } // Для неправильних шляхів
        />
      </Routes>
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <Router style={{ backgroundColor: "var(--background-color)" }}>
        <AppContent />
      </Router>
    </AuthProvider>
  );
};

export default App;
